import {
    AfterContentChecked,
    Component,
    OnDestroy,
    OnInit,
    ViewChild,
    ViewContainerRef,
} from '@angular/core';
import {StepsWizardAdapter, StepsWizardEntry} from '@component/steps-wizard/steps-wizard.adapter';
import {RoleEnum} from '@core/security/enum/role.enum';
import {ButtonTypeEnum} from '@shared/button/button.directive';
import {ChapterInterface} from '@shared/interfaces/chapter.interface';
import {ActivatedRoute, Data} from '@angular/router';
import {BookDetailInterface} from '@shared/interfaces/book.interface';
import {AppExamMetadata, AppExamPlayerService, AppExamStep, AppExamType} from '@component/exam/player/player.service';
import {ApiDocumentDataInterface} from '@shared/modules/content/interfaces/api_v1';
import {OperatorFunction, Subscription} from 'rxjs';
import {DocumentAssignmentService} from '@shared/modules/content/services/assignment/assignment.service';
import {
    PaginationButton,
    PaginationEventType,
    PaginationMappedPageEvent,
} from '@component/pagination/pagination.component';
import {filter, take} from 'rxjs/operators';
import {BreadCrumbInterface} from '@shared/interfaces/breadcrumb.interface';
import {BreadcrumbActions} from '@store/breadcrumb';
import {sprintf} from 'sprintf-js';
import {Store} from '@ngrx/store';
import {RoutingEvent, RoutingService} from '@component/routing/routing.service';
import {AssignmentContentType, AssignmentInterface} from '@shared/interfaces/assignment.interface';
import {HeaderTitleService} from '@component/header-title/header-title.service';
import {AuthorizationService} from '@core/security/services/authorization.service';
import {AssignmentAnswersStateService} from '@shared/modules/content/services/show-answers/answers-state.service';
import {LibrarySelectors} from '@store/library';
import {ReadSpeakerService} from '@feature/chapters/components/readspeaker-menu/service/readspeaker.service';
import {DocumentAnswersService} from '@shared/modules/content/services/answers/answers.service';
import {AppExamActions} from '@component/exam/redux/exam.actions';
import {AppExamService} from '@component/exam/exam.service';
import {PaginationService} from '@component/pagination/service/pagination.service';

export interface AppExamQueryParams {
    userId?: number;
}

export interface AppExamActivatedRouteData {
    book: BookDetailInterface;
    chapter: ChapterInterface;
    examMetadata: AppExamMetadata;
    queryParams: AppExamQueryParams;
}

enum AppExamStepsWizardEntryCustomValue {
    Finish = '<span class="icon icon--results-student"><span class="path1"></span><span class="path2"></span><span class="path3"></span><span class="path4"></span></span>',
}

export enum AppExamPaginationLabels {
    Previous = 'steps-wizard.labels.previous',
    Next = 'steps-wizard.labels.next',
    Finish = 'content-exam.labels.finish',
    Results = 'content-exam.labels.result',
    Reset = 'content-exam.labels.reset',
    Retry = 'content-exam.labels.retry',
    Close = 'content-exam.labels.close',
}

@Component({
    selector: 'app-content-exam-player',
    templateUrl: './player.component.html',
    styleUrls: ['./player.component.scss'],
})
export class AppExamPlayerComponent implements OnInit, AfterContentChecked, OnDestroy {
    readonly #subscriptions: Subscription[] = [];

    readonly #authorizationService: AuthorizationService;

    readonly #routingService: RoutingService;

    readonly #activatedRoute: ActivatedRoute;

    readonly #contentExamService: AppExamPlayerService;

    readonly #documentAssignmentService: DocumentAssignmentService;

    readonly #assignmentAnswersStateService: AssignmentAnswersStateService;

    readonly #readSpeakerService: ReadSpeakerService;

    readonly #store: Store;

    readonly #headerTitleService: HeaderTitleService;

    #teacherLocalAnswers: Set<number> = new Set();

    #assignment?: AssignmentInterface;

    #hidePaginationNavigation: boolean = false;

    #hasPendingMetadataUpdate: boolean = false;

    #pendingPageUpdate: number | null = null;

    #previousExamStep?: AppExamStep;

    readonly roleEnum = RoleEnum;

    readonly buttonType = ButtonTypeEnum;

    @ViewChild('componentAnchor')
    readonly anchor!: ViewContainerRef;

    book: BookDetailInterface | undefined

    chapter: ChapterInterface | undefined;

    userId?: number;

    examMetadata!: AppExamMetadata;

    adapter!: StepsWizardAdapter;

    showAnswer: boolean = false;

    legacyDocument!: ApiDocumentDataInterface;

    isLoading: boolean = false;

    showResults: boolean = false;

    currentPage?: number;

    showWarningMessage: boolean = false;

    isTeacher: boolean = false;

    constructor(
        authorizationService: AuthorizationService,
        routingService: RoutingService,
        activatedRoute: ActivatedRoute,
        contentExamService: AppExamPlayerService,
        documentAssignmentService: DocumentAssignmentService,
        assignmentAnswersStateService: AssignmentAnswersStateService,
        headerTitleService: HeaderTitleService,
        store: Store,
        readSpeakerService: ReadSpeakerService,
        private documentAnswersService: DocumentAnswersService,
        private examService: AppExamService,
        private paginationService: PaginationService,
    ) {
        this.#authorizationService = authorizationService;
        this.#routingService = routingService;
        this.#activatedRoute = activatedRoute;
        this.#contentExamService = contentExamService;
        this.#documentAssignmentService = documentAssignmentService;
        this.#assignmentAnswersStateService = assignmentAnswersStateService;
        this.#headerTitleService = headerTitleService;
        this.#store = store;
        this.#readSpeakerService = readSpeakerService;

        this.isTeacher = this.#authorizationService.isGranted(RoleEnum.RoleTeacher);
    }

    ngOnInit(): void {
        this.#subscriptions.push(
            this.#contentExamService.subscribeToStep((examStep: AppExamStep) => this.#handleExamStepNext(examStep)),
            this.#contentExamService.subscribeToMetadata((metadata: AppExamMetadata) => this.#load(metadata)),
            this.#store.select(LibrarySelectors.selectBook).pipe(
                filter(book => book !== undefined) as OperatorFunction<BookDetailInterface | undefined, BookDetailInterface>,
            ).subscribe((book: BookDetailInterface) => {
                this.book = book;
                this.#loadBreadCrumbs();
            }),
            this.#store
                .select(LibrarySelectors.selectChapter)
                .pipe(filter(chapter => chapter !== undefined) as OperatorFunction<ChapterInterface | undefined, ChapterInterface>,
                ).subscribe((chapter: ChapterInterface) => {
                    this.chapter = chapter;
                    this.#loadBreadCrumbs();
                }),
            this.documentAnswersService.subscribeToExamAnswers((answer) => {
                this.#hasPendingMetadataUpdate = true;
                if (this.isTeacher) {
                    this.#updateAdapterEntries();

                    return;
                }

                if (this.currentPage === this.adapter.entries.length - 1) {
                    this.#updateMetadata();
                }
            }),
        );

        this.paginationService.warning$.subscribe(message => {
            if (message) {
                this.showWarningMessage = true;
                setTimeout(() => {
                    this.showWarningMessage = false;
                    this.paginationService.clearWarning();
                }, 3000);
            }
        });

        this.#activatedRoute.data.pipe(take(1) as OperatorFunction<Data, AppExamActivatedRouteData>).subscribe((data: AppExamActivatedRouteData): void => {
            this.userId = data.queryParams.userId; // @see https://xiponlineapplications.atlassian.net/browse/BD-1995
        });
    }

    ngAfterContentChecked(): void {
        this.#headerTitleService.setHeaderTitle(this.examMetadata.title + ' | ' + this.book?.title + (this.book?.subTitle ? ' | ' + this.book?.subTitle : ''));
    }

    ngOnDestroy(): void {
        this.#subscriptions.forEach((subscription: Subscription) => subscription.unsubscribe());
        this.#contentExamService.clear();
    }

    handleShowAnswer(): void {
        if (undefined === this.#assignment) {
            return;
        }

        this.showAnswer = this.#assignmentAnswersStateService.toggleAnswerModel(this.#assignment, !this.showAnswer);
    }

    handleCloseExam(): void {
        this.#routingService.subscribeToRoutingEvent((event: RoutingEvent): void => this.#routingService.navigate(event.previous ?? ['books', this.book?.uuid ?? '']), true);
    }

    #load(metadata: AppExamMetadata): void {
        this.examMetadata = metadata;
        this.adapter?.destroy();

        const isTeacher: boolean = this.#authorizationService.isGranted(RoleEnum.RoleTeacher);
        const steps: StepsWizardEntry[] = [...Array(metadata.assignments.length)].map((value: undefined, index: number): StepsWizardEntry => ({
            index,
            hasAnswer: metadata.assignments[index].hasAnswer,
            inProgress: metadata.inProgress,
            isTeacher: isTeacher,
        }));
        const hidePaginationNavigation: boolean = this.#hidePaginationNavigation = !isTeacher && AppExamType.Real === metadata.type && 0 !== (metadata.results?.length ?? 0);

        steps.push({
            value: AppExamStepsWizardEntryCustomValue.Finish,
            index: steps.length,
            disabled: metadata.inProgress,
            hidden: isTeacher && undefined === this.userId,
            hasAnswer: false,
            inProgress: metadata.inProgress,
            isTeacher: isTeacher,
        });

        const startPage = this.currentPage ?? this.#getStartPage(metadata);
        const adapter: StepsWizardAdapter = this.adapter = StepsWizardAdapter.Builder(steps)
            .setCurrentPage(startPage)
            .setPaginationMaxSize(isTeacher ? 7 : 6)
            .setHidePages(hidePaginationNavigation)
            .build();

        adapter.subscribeToPageChange((page: number) => {
            if (this.currentPage === page) {
                this.#updatePaginationPreviousButton(page)
                this.#updatePaginationNextButton(page)

                return;
            }

            this.currentPage = page;
            this.#handlePageChange(page);
        });

        adapter.subscribeToMappedPageEvent((event: PaginationMappedPageEvent) => this.#handleMappedPageEvent(event));
    }

    #checkAllEntriesAnswered(): boolean {
        return this.adapter?.entries?.slice(0, -1).every(e => e.index !== undefined && e.hasAnswer) ?? false;
    }

    #getStartPage(metadata: AppExamMetadata): number {
        const lastEntryWithAnswer: AssignmentInterface = metadata.assignments.reduce(
            (current: AssignmentInterface, next: AssignmentInterface): AssignmentInterface => next.hasAnswer ? next : current,
        );

        return (metadata.inProgress || undefined === metadata.results || 0 === metadata.results.length)
            ? metadata.assignments.indexOf(lastEntryWithAnswer) + 1
            : metadata.assignments.length + 1;
    }

    #handleExamStepNext(examStep: AppExamStep): void {
        const assignment: AssignmentInterface = this.#assignment = examStep.document.assignment;
        const hasCorrectAnswer = assignment?.answersV2 && assignment?.answersV2?.correctAnswers ? assignment?.answersV2?.correctAnswers?.length > 0 : false;
        const showResult = this.#assignmentAnswersStateService.getShowResults();

        // We should not have to trigger this when assignments are properly loaded through documents,
        // this step also complicates setting show answers in state. It triggers a state change which
        // will override the `showAnswer` state.
        this.#documentAssignmentService.selectByDpsIdSuccess(assignment);
        this.legacyDocument = examStep.document.legacyDocument;
        this.isLoading = false;
        this.#readSpeakerService.triggerRefresh();

        if (!this.#previousExamStep || this.#hasExamStepChanged(this.#previousExamStep, examStep)) {
            this.#previousExamStep = examStep;
            this.#updateMetadata();
        }

        if (this.showAnswer || (showResult && hasCorrectAnswer && assignment.contentType === AssignmentContentType.Exam)) {
            setTimeout(() => {
                this.#assignmentAnswersStateService.showAnswerModel(assignment);
                this.showAnswer = true;
            }, 50);
        }
    }

    #updateMetadata(): void {
        if (this.#hasPendingMetadataUpdate) {
            this.#pendingPageUpdate = this.adapter.currentPage;
            const examPlanId = this.examMetadata.type === AppExamType.Real ? this.examMetadata.examPlanId : undefined;

            this.examService.fetchExamData(this.#activatedRoute.snapshot, examPlanId).subscribe((data) => {
                this.#store.dispatch(AppExamActions.metadataSuccess({metadata: data}));

                this.#hasPendingMetadataUpdate = false;

                if (this.#pendingPageUpdate !== null) {
                    this.adapter.currentPage = Number(this.#pendingPageUpdate);
                    this.#pendingPageUpdate = null;
                }
            });
        }
    }

    #updateAdapterEntries(): void {
        const page = this.adapter.currentPage - 1;

        this.#teacherLocalAnswers.add(page);

        this.adapter.entries.forEach(entry => {
            if (this.#teacherLocalAnswers.has(entry.index)) {
                entry.hasAnswer = true;
            }
        });
    }

    #hasExamStepChanged(prev: AppExamStep, next: AppExamStep): boolean {
        return prev.bookUuid !== next.bookUuid ||
            prev.chapterUuid !== next.chapterUuid ||
            prev.document.dpsId !== next.document.dpsId;
    }

    /**
     * This method is triggered as soon as the pagination loads
     */
    #handlePageChange(page: number): void {
        const adapterEntries: number = this.adapter.entries.length;
        const metadata: AppExamMetadata = this.examMetadata;
        const inProgress: boolean = metadata.inProgress;
        const isTeacher: boolean = this.#authorizationService.isGranted(RoleEnum.RoleTeacher);

        this.currentPage = page;

        // It's possible that the teacher is viewing the user's results. If so, display results instead.
        const isCurrentUser: boolean = undefined !== metadata.user ? this.#authorizationService.isCurrentUser(metadata.user) : true;

        this.showResults = page === adapterEntries && !metadata.inProgress;

        if (this.showResults) {
            this.#assignmentAnswersStateService.setShowResults(this.showResults);
            this.showAnswer = true;
            this.isLoading = false;
        }

        this.isLoading = !this.showResults;
        if (page === adapterEntries) {
            this.isLoading = false;
            this.showResults = true;
            this.showAnswer = true;
        }

        if (!inProgress && isCurrentUser && 0 === (metadata.results ?? []).length) {
            // Auto start exam when not in progress and without results
            this.#contentExamService.start(metadata).subscribe((): void => this.#handlePageChangeReady(page));
        } else if (!isTeacher && page === adapterEntries && inProgress) {
            // Only students can finish exams, this goes for practise and official exams
            this.#contentExamService.finish(metadata).subscribe((): void => this.#handlePageChangeReady(page));
        } else if (isTeacher && isCurrentUser && page === adapterEntries) {
            // Close exam for teacher because they cannot finish own exams, thus cannot generate results
            this.handleCloseExam();
        } else {
            this.#contentExamService.load(page, this.userId).subscribe((): void => this.#handlePageChangeReady(page));
        }
    }

    #handlePageChangeReady(page: number): void {
        this.#updatePaginationPreviousButton(page);
        this.#updatePaginationNextButton(page);
    }

    #updatePaginationPreviousButton(page: number): void {
        this.adapter.updatePreviousButton({
            value: AppExamPaginationLabels.Previous,
            disabled: 1 === page,
            visible: !this.#hidePaginationNavigation,
        });
    }

    #updatePaginationNextButton(page: number): void {
        const inProgress: boolean = this.examMetadata.inProgress;
        const isTeacher: boolean = this.#authorizationService.isGranted(RoleEnum.RoleTeacher);
        const button: PaginationButton = {value: AppExamPaginationLabels.Next};

        if (!isTeacher && page === (this.adapter.entries.length - 1)) {
            button.value = inProgress ? AppExamPaginationLabels.Finish : AppExamPaginationLabels.Results;
        }

        if (isTeacher && page === (this.adapter.entries.length - 1)) {
            button.value = undefined === this.userId ? AppExamPaginationLabels.Close : AppExamPaginationLabels.Results;
        }

        if (page === this.adapter.entries.length) {
            button.value = !isTeacher && AppExamType.Practice == this.examMetadata.type ? AppExamPaginationLabels.Retry : AppExamPaginationLabels.Close;
        }

        const isLastPage: boolean = page === this.adapter.entries.length -1;

        if (isLastPage) {
            const btn = {
                value: button.value,
                disabled: !this.#checkAllEntriesAnswered(),
            };

            this.adapter.updateNextButton(btn);

            return;
        }

        this.adapter.updateNextButton(button);
    }

    /**
     * This function handles custom button events, triggered by buttons added at this.#load: AppExamStepsWizardEntryCustomValue.Finish
     */
    #handleMappedPageEvent(event: PaginationMappedPageEvent): void {
        if (event.pageChanged || PaginationEventType.Next !== event.type || AppExamStepsWizardEntryCustomValue.Finish !== event.button.value) {
            return;
        }

        const isTeacher: boolean = this.#authorizationService.isGranted(RoleEnum.RoleTeacher);

        if (!isTeacher && AppExamType.Practice == this.examMetadata.type) {
            this.isLoading = false;
            this.#contentExamService.start(this.examMetadata).subscribe((): void => {
                this.showAnswer = false;
                this.showResults = false;
                this.adapter.currentPage = 1;
            });
        } else {
            this.handleCloseExam();
        }
    }

    #loadBreadCrumbs(): void {
        if (undefined === this.book || undefined === this.chapter) {
            return;
        }

        const breadcrumbs: BreadCrumbInterface[] = [
            {label: 'breadcrumbs.books', translateForPublisher: true, url: '/books'},
            {label: this.book.title, url: sprintf('/books/%s', this.book.uuid)},
            {label: this.chapter.title, url: sprintf('/books/%s', this.chapter.uuid)},
        ];

        this.#store.dispatch(BreadcrumbActions.setBreadcrumbsAction({breadcrumbs}));
    }
}
